import React, { useState } from "react";
import { Loader2, X } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { useTenant } from "@/lib/TenantContext";
// Relative, not "@/...": real modules under Jest, no alias there.
import useCheckoutQuote from "../../lib/useCheckoutQuote";
import PriceBreakdown from "./PriceBreakdown";
import PromoField from "./PromoField";
import GiftCardField from "./GiftCardField";
import MarketingOptIn from "../consent/MarketingOptIn";

/**
 * PurchasePanel — THE STEP BETWEEN CHOOSING AN ITEM AND PAYING FOR IT (BUG-049).
 *
 * WHY IT EXISTS. The shop and the programmes page used to carry ONE promo field and ONE
 * gift-card field above a whole catalogue, before anything had been chosen. A code could
 * therefore be "accepted" with no item to apply it to; a card could only ever show its
 * balance, because there was no purchase amount to quote it against; and both widgets were
 * handed the currency of the FIRST listed row rather than of the row being bought. This
 * panel is the missing step: the buyer picks the item, and only then are the instruments
 * offered — against that item's price, in that item's currency.
 *
 * IT ALSO CARRIES THE DISCLOSURE (BUG-048/BUG-054). `useCheckoutQuote` asks the server
 * what this purchase will cost — discount, each named tax line, total — and
 * `PriceBreakdown` prints it. The gift card is then quoted against
 * `total_charged_cents`, the amount that will really be charged, instead of the pre-tax
 * price: that is what makes "covers X · Y left to pay" true rather than a floor.
 *
 * IT ASKS THE OPTIONAL MARKETING QUESTION (owner decision 2026-10-21): the unticked "send
 * me news and offers from {practice}" box. `kind` names it (product | program), and the
 * answer travels back with the codes — the server records a tick only once the sale is
 * paid.
 *
 * WHAT IT DOES NOT DO. It never opens a checkout itself. `onPay({ promoCode,
 * giftCardCode, marketingOptIn })` hands the two accepted codes and the buyer's marketing
 * answer back to the page, which owns its own endpoint, its own return paths and its own
 * auth gate (`useRequireAuth`) — so there is exactly one checkout request per purchase and
 * no half-applied state to reconcile.
 *
 * Props:
 *   name         what the buyer is buying, already localised by the page
 *   priceCents   the advertised price of that item
 *   currency     ISO currency of that item (never a default of this component's)
 *   kind         the purchase kind for the promo scope + the quote: "product" | "program"
 *   offeringType the catalogue the row lives in (same two values here)
 *   offeringId   the row's id, so the quote honours its own tax treatment
 *   payLabel     the page's own CTA wording ("Buy", "Enroll") — no new copy invented here
 *   busy         a checkout is in flight (the page owns that state)
 *   onPay        ({promoCode, giftCardCode, marketingOptIn}) => void
 *   onClose      () => void
 */
export default function PurchasePanel({
  name,
  priceCents,
  currency,
  kind,
  offeringType,
  offeringId,
  payLabel,
  busy = false,
  onPay,
  onClose,
}) {
  const t = useT("public");
  const tc = useT("common");
  const { slug } = useTenant();
  const [promoCode, setPromoCode] = useState("");
  const [giftCardCode, setGiftCardCode] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const { quote, error } = useCheckoutQuote({
    serviceType: kind,
    offeringType,
    offeringId,
    amountCents: priceCents,
    tenantSlug: slug,
    promoCode,
  });

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-eh-bg rounded-3xl max-w-md w-full p-7 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="purchase-panel"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-eh-muted">
              {t("purchase.title")}
            </div>
            <h2 className="font-display text-2xl mt-1" data-testid="purchase-item-name">{name}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tc("actions.cancel")}
            className="p-1 rounded-full text-eh-muted hover:text-eh-ink"
            data-testid="purchase-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-5">
          <PriceBreakdown quote={quote} error={error} currency={currency} />
        </div>

        <div className="mt-4 space-y-3">
          <PromoField
            kind={kind}
            value={promoCode}
            onChange={setPromoCode}
            currency={currency}
            disabled={busy}
          />
          {/* THE TAXED TOTAL, not the shelf price: the card is a tender against what will
              be charged, and quoting it against the pre-tax figure understated what it
              covers. `0` while the quote is unknown, which puts the field in its
              balance-only mode rather than inventing a residual. */}
          <GiftCardField
            value={giftCardCode}
            onChange={setGiftCardCode}
            amountCents={quote?.total_charged_cents || 0}
            currency={currency}
            disabled={busy}
          />
          <MarketingOptIn
            form={kind}
            checked={marketingOptIn}
            onChange={setMarketingOptIn}
            disabled={busy}
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">
            {tc("actions.cancel")}
          </button>
          <button
            type="button"
            onClick={() => onPay({ promoCode, giftCardCode, marketingOptIn })}
            disabled={busy}
            className="btn-primary text-sm disabled:opacity-60"
            data-testid="purchase-pay"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} {payLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
